export const generateTopicUrl = (topic: {
  slug: string;
  categories?: {
    slug: string;
    level?: number;
    parent_category_id?: string;
    parent_category?: {
      slug: string;
      parent_category?: {
        slug: string;
      };
    };
  };
}) => {
  if (!topic.categories) return `/topic/${topic.slug}`;
  
  const cat = topic.categories;
  
  // Topic in Level 3 category: /level1-slug/level2-slug/level3-slug/topic-slug
  if (cat.level === 3 && cat.parent_category?.parent_category) {
    return `/${cat.parent_category.parent_category.slug}/${cat.parent_category.slug}/${cat.slug}/${topic.slug}`;
  }
  
  // Topic in Level 2 category: /level1-slug/level2-slug/topic-slug
  if (cat.level === 2 && cat.parent_category) {
    return `/${cat.parent_category.slug}/${cat.slug}/${topic.slug}`;
  }
  
  // Fallback for hierarchical structure based on parent relationships
  if (cat.parent_category_id && cat.parent_category) {
    if (cat.parent_category.parent_category) {
      // Has grandparent: /grandparent/parent/category/topic
      return `/${cat.parent_category.parent_category.slug}/${cat.parent_category.slug}/${cat.slug}/${topic.slug}`;
    }
    // Has parent only: /parent/category/topic
    return `/${cat.parent_category.slug}/${cat.slug}/${topic.slug}`;
  }
  
  // Level 1 or standalone: /category/topic
  return `/${cat.slug}/${topic.slug}`;
};

export const generateCategoryUrl = (category: {
  slug: string;
  level?: number;
  parent_category_id?: string;
  parent_category?: {
    slug: string;
    parent_category?: {
      slug: string;
    };
  };
}) => {
  // Level 3 category: /level1-slug/level2-slug/level3-slug
  if (category.level === 3 && category.parent_category?.parent_category) {
    return `/${category.parent_category.parent_category.slug}/${category.parent_category.slug}/${category.slug}`;
  }
  
  // Level 2 category: /level1-slug/level2-slug
  if (category.level === 2 && category.parent_category) {
    return `/${category.parent_category.slug}/${category.slug}`;
  }
  
  // Fallback for hierarchical structure based on parent relationships
  if (category.parent_category_id && category.parent_category) {
    if (category.parent_category.parent_category) {
      // Has grandparent: /grandparent/parent/category
      return `/${category.parent_category.parent_category.slug}/${category.parent_category.slug}/${category.slug}`;
    }
    // Has parent only: /parent/category
    return `/${category.parent_category.slug}/${category.slug}`;
  }
  
  // Level 1 or standalone: /category
  return `/${category.slug}`;
};

export const generateSlugFromTitle = (title: string): string => {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
    .replace(/\s+/g, '-') // Replace spaces with dashes
    .replace(/-+/g, '-') // Replace multiple dashes with single dash
    .trim();
};

// Enhanced URL reconstruction that preserves original structure
export const reconstructPreservingOriginal = (oldUrl: string): string | null => {
  // Extract topic ID (critical for preservation)
  const topicIdMatch = oldUrl.match(/-t(\d+)\.html$/);
  if (!topicIdMatch) return null;
  
  const topicId = topicIdMatch[1];
  
  // Extract components while preserving original structure
  const filename = oldUrl.split('/').pop()?.replace('.html', '') || '';
  const year = filename.match(/20\d{2}/)?.[0];
  const levelMatch = filename.match(/-a{1,3}(?![a-z])/i);
  const level = levelMatch ? levelMatch[0].replace('-', '').toLowerCase() : null;
  
  // Extract organization
  const organization = filename.includes('gthl') ? 'gthl' :
                      filename.includes('alliance') ? 'alliance' :
                      filename.includes('ontario') ? 'ontario' : null;
  
  if (year && level) {
    // Build category path
    const categoryPath = organization ? 
      `${organization}-${year}-${level}` : 
      `ontario-${year}-${level}`;
    
    // Build topic slug preserving most of original filename with topic ID
    const topicSlug = `${filename}-t${topicId}`;
    
    return `/${categoryPath}/${topicSlug}`;
  }
  
  return null;
};
